import styled from '@emotion/styled';
import { useEffect } from 'react';
import { useUpdate } from 'react-use';
import { MenuButton } from '~/modules/Elements/Menu';
import MenuWithLogo from '~/modules/Elements/MenuWithLogo';
import events from '~/modules/GameEvents/GameEvents';
import { useEventListenerSelector } from '~/modules/GameEvents/hooks';
import useBackgroundMusic from '~/modules/hooks/useBackgroundMusic';
import useKeyboardNav from '~/modules/hooks/useKeyboardNav';
import useSmoothNavigate from '~/modules/hooks/useSmoothNavigate';
import RemoteMicManager from '~/modules/RemoteMic/RemoteMicManager';
import {
  DefaultRemoteMicPermission,
  RemoteMicConnectionType,
  RemoteMicConnectionTypeSetting,
  RemoteMicPermissions,
  UnassignOnSongFinishedSetting,
  useSettingValue,
} from '~/routes/Settings/SettingsState';

import { Checkbox } from '~/modules/Elements/AKUI/Checkbox';
import { nextValue } from '~/modules/Elements/Utils/indexes';
import { GAME_CODE_LENGTH, storeGameCode } from '~/modules/RemoteMic/Network/Server/NetworkServer';
import { useDevicePing } from '~/routes/SelectInput/hooks/useDevicePing';

function RemoteMicSettings() {
  useBackgroundMusic(false);
  const navigate = useSmoothNavigate();
  const goBack = () => navigate('settings/');

  const { register } = useKeyboardNav({ onBackspace: goBack });

  const [remoteMicConnectionType, setRemoteMicConnectionType] = useSettingValue(RemoteMicConnectionTypeSetting);
  const [defaultPermission, setDefaultPermission] = useSettingValue(DefaultRemoteMicPermission);
  const [unassignOnSongFinished, setUnassignOnSongFinished] = useSettingValue(UnassignOnSongFinishedSetting);
  const remoteMics = useEventListenerSelector(events.inputListChanged, () => RemoteMicManager.getRemoteMics());

  const forceUpdate = useUpdate();
  useEffect(() => RemoteMicManager.addListener(forceUpdate), [forceUpdate]);

  return (
    <MenuWithLogo>
      <Title
        onClick={() => {
          const code = prompt('Code');

          if (code?.length === GAME_CODE_LENGTH - 1) {
            storeGameCode(code);
            global.location.reload();
          }
        }}
      >
        Remote Microphone Settings
      </Title>

      {/* ✅ Reemplazamos Switcher por un botón responsive (evita solapes en móvil) */}
      <SettingButton
        {...register('connection type', () =>
          setRemoteMicConnectionType(nextValue(RemoteMicConnectionType, remoteMicConnectionType)),
        )}
      >
        <SettingRow>
          <SettingLabel>Connection type</SettingLabel>
          <SettingValue>{remoteMicConnectionType}</SettingValue>
        </SettingRow>
      </SettingButton>

      <hr />

      <SettingButton
        {...register('default-permission', () => setDefaultPermission(nextValue(RemoteMicPermissions, defaultPermission)))}
        info={
          <>
            <strong>WRITE</strong> - player is able to navigate the menus remotely and assign themselves and other
            players to the game.
            <br />
            <strong>READ</strong> - player will only be able to use the device as a microphone and otherwise have no
            control over the game.
          </>
        }
      >
        <SettingRow>
          <SettingLabel>Default permission</SettingLabel>
          <SettingValue>{defaultPermission.toUpperCase()}</SettingValue>
        </SettingRow>
      </SettingButton>

      <Checkbox
        size="small"
        checked={unassignOnSongFinished}
        {...register('unassign-on-song-finished', () => setUnassignOnSongFinished(!unassignOnSongFinished))}
        info={`Prevents "ghost" players - remote mics sticking around, not singing subsequent songs.`}
      >
        Unassign players after they finish singing
      </Checkbox>

      <hr />

      <SectionTitle>Connected devices permissions:</SectionTitle>

      {remoteMics.map((mic) => {
        const permission = RemoteMicManager.getPermission(mic.id);

        return (
          <MicPermissionButton
            key={mic.id}
            {...register(
              `mic-${mic.id}`,
              () => RemoteMicManager.setPermission(mic.id, nextValue(RemoteMicPermissions, permission)),
              'Change permissions',
            )}
            data-test="remote-mic-entry"
            data-id={mic.id}
            data-permission={permission}
          >
            <MicRow>
              <MicLeft>
                <RemoteMicId>{mic.id.slice(-4)}</RemoteMicId>
                <RemoteMicPing>
                  <DevicePing deviceId={mic.id} />
                </RemoteMicPing>
                <RemoteMicName className="ph-no-capture">{mic.name}</RemoteMicName>
              </MicLeft>

              <MicRight>
                <MicPermissionPill>{permission.toUpperCase()}</MicPermissionPill>
              </MicRight>
            </MicRow>
          </MicPermissionButton>
        );
      })}

      {remoteMics.length === 0 && <h4>No remote microphones connected</h4>}

      <hr />

      <MenuButton {...register('back-button', goBack)}>Return To Settings</MenuButton>
    </MenuWithLogo>
  );
}

const DevicePing = ({ deviceId }: { deviceId: string }) => {
  const latency = useDevicePing(deviceId);
  return latency === null ? null : <>{latency}ms</>;
};

/* -------------------- Styles -------------------- */

const Title = styled.h1`
  cursor: pointer;
`;

const SectionTitle = styled.h3`
  text-align: center;
`;

/**
 * Botón tipo “fila de setting” que evita el layout interno de Switcher (que te solapa en móvil).
 */
const SettingButton = styled(MenuButton)`
  width: 100%;
  padding-left: 1.25rem !important;
  padding-right: 1.25rem !important;

  /* Altura cómoda */
  min-height: 5.8rem;

  @media (max-width: 520px) {
    min-height: 6.4rem;
    padding-left: 1rem !important;
    padding-right: 1rem !important;
  }
`;

const SettingRow = styled.div`
  width: 100%;
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
  min-width: 0;

  @media (max-width: 520px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.35rem;
  }
`;

const SettingLabel = styled.span`
  font-weight: 700;
  text-transform: uppercase;
  white-space: nowrap;

  @media (max-width: 520px) {
    white-space: normal;
  }
`;

const SettingValue = styled.span`
  font-weight: 800;
  color: rgba(255, 255, 255, 0.95);
  text-transform: uppercase;
  text-align: right;
  margin-left: auto;
  white-space: nowrap;

  @media (max-width: 520px) {
    margin-left: 0;
    text-align: left;
    white-space: normal;
    opacity: 0.95;
  }
`;

/**
 * Permisos por mic: lo hacemos con MenuButton también (responsive y sin solapes)
 */
const MicPermissionButton = styled(MenuButton)`
  width: 100%;
  padding-left: 1.1rem !important;
  padding-right: 1.1rem !important;
  min-height: 6.2rem;

  @media (max-width: 520px) {
    min-height: 7rem;
  }
`;

const MicRow = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  min-width: 0;
`;

const MicLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.9rem;
  min-width: 0;
  overflow: hidden;
`;

const MicRight = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
`;

const MicPermissionPill = styled.span`
  padding: 0.35rem 0.8rem;
  border-radius: 999px;
  font-weight: 800;
  text-transform: uppercase;
  background: rgba(255, 255, 255, 0.08);
  outline: 1px solid rgba(255, 255, 255, 0.14);
`;

const RemoteMicName = styled.span`
  text-transform: uppercase;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RemoteMicId = styled.span`
  font-size: 1.35rem;
`;

const RemoteMicPing = styled.span`
  font-size: 1.25rem;
  opacity: 0.85;
  text-transform: none;
`;

export default RemoteMicSettings;
